
// client/src/components/media/ScheduledPostsList.jsx
// Read-only + cancel management UI for upcoming scheduled social posts.
//
// Uses your existing /api/schedule/upcoming/:tenantId and DELETE /api/schedule/:postId endpoints,
// and the worker stack still handles actually publishing posts.

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  fetchUpcomingScheduledPosts,
  cancelScheduledPost,
} from "../../services/scheduledPostsApi";
// import MediaGrid from "./MediaGrid"; // Temporarily disabled
import AppFooter from "../AppFooter";
import {
  FaArrowLeft,
  FaCalendarPlus,
  FaTrash,
  FaEye,
  FaList,
  FaSync,
} from "react-icons/fa";
import "./ScheduledPostsList.css";

/**
 * Helper to safely format dates coming back from MySQL/Node.
 */
function formatDate(value) {
  if (!value) return "—";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) {
      return String(value);
    }
    return d.toLocaleString();
  } catch {
    return String(value);
  }
}

/**
 * Helper to format date in short format for embedded display
 * Shows date and time in a compact format
 */
function formatDateShort(value) {
  if (!value) return "—";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) {
      return String(value);
    }
    // Format as "Jan 11, 3:45 PM"
    return d.toLocaleDateString(undefined, { 
      month: 'short', 
      day: 'numeric' 
    }) + ', ' + d.toLocaleTimeString(undefined, { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  } catch {
    return String(value);
  }
}

/**
 * Parse MySQL-ish datetime safely.
 * Handles:
 *  - Date objects
 *  - ISO strings
 *  - "YYYY-MM-DD HH:mm:ss" (common MySQL DATETIME)
 */
function parseDateMaybeMySQL(value) {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const raw = String(value);

  const normalized =
    /\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(raw) && !raw.includes("T")
      ? raw.replace(" ", "T")
      : raw;

  const d = new Date(normalized);
  if (!Number.isNaN(d.getTime())) return d;

  const fallback = new Date(raw);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

/**
 * Compare at "date" granularity (local day start) to avoid time-of-day quirks.
 */
function toLocalDayStart(dateLike) {
  const d = parseDateMaybeMySQL(dateLike);
  if (!d) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * A post can be cancelled while it's in the queue / being picked up,
 * but not after it's already been posted.
 */
function isCancellable(post) {
  const status = String(post?.status || "").toLowerCase();
  return status === "queued" || status === "posting";
}

/**
 * Truncate text to a maximum length, adding ellipsis if needed
 */
function truncateText(text, maxLength) {
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + "…";
}

// NOTE: we add `embedded = false` so the component can render inside the Dashboard
// without its own footer, but all existing standalone behavior is unchanged.
export default function ScheduledPostsList({
  tenantId: tenantIdProp,
  embedded = false,
}) {
  const { tenant: tenantFromRoute } = useParams();
  const navigate = useNavigate();

  // Resolve numeric tenantId – same pattern as SchedulePost:
  // Number(:tenant) || 1 as a fallback.
  const tenantId = useMemo(
    () => Number(tenantIdProp ?? tenantFromRoute) || 1,
    [tenantIdProp, tenantFromRoute]
  );

  const [posts, setPosts] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    // NOTE: embedded view *displays* 3, but we may fetch more to keep the list filled after filtering.
    limit: embedded ? 25 : 50,
    offset: 0,
    hasMore: false,
  });
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [cancelingId, setCancelingId] = useState(null);

  // Choose a wrapper class:
  // - Standalone: Bootstrap container
  // - Embedded: slim wrapper that fits nicely in Dashboard card cell
  const wrapperClass = embedded
    ? "dashboard-scheduled-container"
    : "container py-3";

  /**
   * Cutoff = START OF TODAY at local day granularity.
   *
   * Requirement:
   *  - Show posts scheduled for today and future dates
   *  - Hide posts scheduled in the past (before today)
   *
   * Scheduled posts schema context:
   * - t_social_post.scheduled_on is when the post should be published
   */
  const cutoffDay = useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }, []);

  /**
   * Core loader – can load the first page or append "load more" pages.
   */
  const loadPage = useCallback(
    async ({ append = false, offsetOverride } = {}) => {
      if (!tenantId) return;

      // Dashboard (embedded) should DISPLAY 3, but we FETCH more to survive filtering.
      const limit = embedded ? 25 : 50;

      const offset =
        offsetOverride ?? (append ? pagination.offset + limit : 0);

      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError("");

      try {
        const data = await fetchUpcomingScheduledPosts(tenantId, {
          limit,
          offset,
        });

        const nextPosts = Array.isArray(data?.posts) ? data.posts : [];

        const nextPagination = data?.pagination || {
          total: nextPosts.length,
          limit,
          offset,
          hasMore: false,
        };

        setPosts((prev) => (append ? [...prev, ...nextPosts] : nextPosts));
        setPagination(nextPagination);
      } catch (err) {
        console.error("Failed to load scheduled posts", err);
        setError(err?.message || "Failed to load scheduled posts.");
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [tenantId, pagination.limit, pagination.offset, embedded]
  );

  // Initial load + reload when tenant changes
  useEffect(() => {
    loadPage({ append: false, offsetOverride: 0 });
  }, [tenantId]);

  /**
   * DASHBOARD FILTERING (embedded mode only)
   * - Uses status + scheduled_on to hide "not active" items.
   *
   * Schema:
   * - t_social_post.status: queued|posting|posted|failed|cancelled
   * - t_social_post.scheduled_on: when to publish
   */
  const visiblePosts = useMemo(() => {
    if (!embedded) return posts;

    const allowedStatuses = new Set(["queued", "posting"]);

    const filtered = (posts || []).filter((p) => {
      if (!p) return false;

      const statusLower = String(p.status || "").toLowerCase().trim();

      // "Active" scheduled posts on the Dashboard = queued/posting only
      if (statusLower && !allowedStatuses.has(statusLower)) return false;

      // Scheduled time is the effective date for visibility
      const scheduledCandidate =
        p.scheduled_on ?? p.scheduledOn ?? p.scheduledAt ?? null;

      // If there's no scheduled date, it's an immediate post - show it!
      if (!scheduledCandidate) return true;
      
      const scheduledDay = toLocalDayStart(scheduledCandidate);
      if (!scheduledDay) return true; // If we can't parse the date, show it

      // Show posts scheduled for today or future (>= today)
      return scheduledDay >= cutoffDay;
    });

    // Dashboard should show only 3 items after filtering
    return filtered.slice(0, 3);
  }, [posts, embedded, cutoffDay]);

  /**
   * Navigate to SchedulePost with this scheduled post as defaults.
   * We pass everything through location.state so SchedulePost can pre-fill.
   *
   * When embedded in the Dashboard, we prefer the event-aware route:
   *   /:tenant/:eventId/:eventItemId/schedulePost
   * When standalone, we use the simpler:
   *   /:tenant/media/schedule
   */
  const handleOpen = (post) => {
    if (!post) return;

    const {
      id: postId,
      eventId,
      eventid,
      eventItemId,
      eventitemid,
      content,
      media_urls,
      platforms,
      scheduled_on,
      status,
    } = post;

    // The user might click a "posted" item in standalone mode to view results.
    // We'll still navigate to SchedulePost so they can see the payload.
    const finalEventId = eventId || eventid;
    const finalItemId = eventItemId || eventitemid;

    // Build a state object that SchedulePost can read
    const stateForSchedule = {
      postId,
      // Include event references if present
      ...(finalEventId && { eventId: finalEventId }),
      ...(finalItemId && { eventItemId: finalItemId }),
      content,
      mediaUrls: media_urls,
      platforms: Array.isArray(platforms) ? platforms : [],
      scheduledOn: scheduled_on,
      status,
    };

    let path = "";
    if (embedded && finalEventId && finalItemId) {
      // Event-item route
      path = `/${tenantFromRoute || tenantId}/${finalEventId}/${finalItemId}/schedulePost`;
    } else if (embedded && finalEventId) {
      // Event-only route
      path = `/${tenantFromRoute || tenantId}/${finalEventId}/schedulePost`;
    } else {
      // Generic route (no event context or standalone mode)
      path = `/${tenantFromRoute || tenantId}/media/schedule`;
    }

    navigate(path, { state: stateForSchedule });
  };

  /**
   * Cancel a scheduled post – prompt + backend call
   */
  const handleCancel = async (post) => {
    if (!post?.id) return;

    const confirm = window.confirm(
      `Cancel scheduled post?
This will remove it from the queue and prevent it from posting.`
    );
    if (!confirm) return;

    setCancelingId(post.id);

    try {
      await cancelScheduledPost(post.id);

      // Remove from local state
      setPosts((prev) => prev.filter((p) => p.id !== post.id));
    } catch (err) {
      console.error("Failed to cancel post", err);
      alert(err?.message || "Failed to cancel post.");
    } finally {
      setCancelingId(null);
    }
  };

  /**
   * Refresh the list by reloading from the beginning
   */
  const onRefresh = useCallback(() => {
    loadPage({ append: false, offsetOverride: 0 });
  }, [loadPage]);

  /**
   * Load the next page of results (pagination)
   */
  const onLoadMore = useCallback(() => {
    loadPage({ append: true });
  }, [loadPage]);

  /**
   * Navigate to standalone ScheduledPostsList to view all posts
   * (used in embedded mode)
   */
  const handleViewAll = () => {
    navigate(`/${tenantFromRoute || tenantId}/media/scheduled-posts`);
  };

  const hasVisiblePosts = visiblePosts.length > 0;

  return (
    <div className={`${wrapperClass} scheduled-posts-theme`}>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h5 className="mb-0">Scheduled Social Posts</h5>
          {!embedded && <small className="text-muted">Tenant #{tenantId}</small>}
        </div>

        {/* Optional refresh button - only in standalone mode */}
        {!embedded && (
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={onRefresh}
            disabled={loading}
          >
            <FaSync className={loading ? "fa-spin me-1" : "me-1"} />
            Refresh
          </button>
        )}
      </div>

      {/* Error banner */}
      {!!error && (
        <div className="alert alert-danger" role="alert">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Loading state */}
      {loading && !hasVisiblePosts && (
        <div className="text-center text-muted py-4">
          Loading scheduled posts…
        </div>
      )}

      {/* Empty state */}
      {!loading && !hasVisiblePosts && !error && (
        <div className="text-center text-muted py-4">
          {embedded 
            ? "No active scheduled posts." 
            : "No scheduled posts found for this tenant."}
        </div>
      )}

      {/* Scheduled posts list */}
      {hasVisiblePosts && (
        <div className="list-group mb-3">
          {visiblePosts.map((post) => {
            // Format platforms for display
            const platforms = Array.isArray(post.platforms)
              ? post.platforms.join(", ")
              : "";
            
            // Format scheduled time - use short format in embedded mode
            const scheduledFor = embedded 
              ? formatDateShort(post.scheduled_on || post.created_on)
              : formatDate(post.scheduled_on || post.created_on);
            
            // Get status for styling
            const status = String(post.status || "").toLowerCase();

            // Get post content preview (up to 25 characters for embedded mode)
            const contentPreview = embedded 
              ? truncateText(post.content, 25)
              : (post.content && post.content.length > 200
                  ? post.content.slice(0, 200) + "…"
                  : post.content);

            // Try to resolve event / item names but fall back to IDs
            const eventName =
              post.eventName ||
              post.eventname ||
              post.event_name ||
              (post.event && (post.event.name || post.event.title)) ||
              (post.eventid ? `Event #${post.eventid}` : "No event");

            const itemName =
              post.itemName ||
              post.itemname ||
              post.eventitemname ||
              post.event_item_name ||
              (post.item && (post.item.name || post.item.title)) ||
              (post.eventitemid ? `Item #${post.eventitemid}` : "");

            return (
              <div
                key={post.id}
                className="list-group-item mb-2 rounded shadow-sm"
                data-platform={platforms.split(',')[0]?.trim().toLowerCase()}
                data-status={status}
                style={{ cursor: "pointer" }}
                onClick={() => handleOpen(post)}
              >
                {/* EMBEDDED MODE: Compact layout matching ScheduledEventsList pattern */}
                {embedded && (
                  <>
                    {/* 
                      REQUIREMENT: Layout structure (matching ScheduledEventsList)
                      Row 1: Content preview (left) | Status badge (right)
                      Row 2: Scheduled date/time (left) | Platform badge (right)
                      NOTE: Left side is CLAMPED to 2 lines total.
                    */}
                    <div className="d-flex justify-content-between align-items-start mb-1">
                      {/* LEFT: exactly 2 lines (content + date), left aligned */}
                      <div className="flex-grow-1 os-post-left">
                        {/* Line 1: Content preview */}
                        <strong className="os-post-title">
                          {contentPreview || "No content"}
                        </strong>

                        {/* Line 2: Scheduled date/time */}
                        <span className="os-post-date">
                          {scheduledFor}
                        </span>
                      </div>

                      {/* RIGHT: badges stacked vertically */}
                      <div className="d-flex flex-column align-items-end gap-1">
                        {/* Status badge on top (QUEUED, POSTING, etc.) */}
                        <span
                          className={
                            "badge text-uppercase " +
                            (status === "queued"
                              ? "bg-warning text-dark"
                              : status === "posting"
                              ? "bg-info"
                              : status === "posted"
                              ? "bg-success"
                              : status.startsWith("fail")
                              ? "bg-danger"
                              : "bg-secondary")
                          }
                        >
                          {status}
                        </span>
                        
                        {/* Platform badge below status (shows first platform) */}
                        {platforms && (
                          <span className="badge bg-secondary text-uppercase">
                            {platforms.split(',')[0]?.trim() || "—"}
                          </span>
                        )}
                      </div>
                    </div>
                  </>
                )}

                {/* STANDALONE MODE: Full layout with all details */}
                {!embedded && (
                  <>
                    {/* Top row: date/time + status */}
                    <div className="d-flex justify-content-between mb-1">
                      <div className="flex-grow-1">
                        <small className="text-muted d-block">{scheduledFor}</small>

                        {/* Event / item names (or fallback labels) - only show in standalone mode */}
                        <div className="small text-muted">
                          {eventName}
                          {itemName ? ` • ${itemName}` : ""}
                        </div>
                      </div>

                      <div className="d-flex flex-column align-items-end gap-1">
                        {/* Status badge with color coding */}
                        <span
                          className={
                            "badge text-uppercase " +
                            (status === "queued"
                              ? "bg-primary"
                              : status === "posting"
                              ? "bg-info"
                              : status === "posted"
                              ? "bg-success"
                              : status.startsWith("fail")
                              ? "bg-danger"
                              : "bg-secondary")
                          }
                        >
                          {status}
                        </span>

                        {/* Cancel button - only for cancellable posts in standalone mode */}
                        {isCancellable(post) && (
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={(e) => {
                              e.stopPropagation(); // prevent opening SchedulePost
                              handleCancel(post);
                            }}
                            disabled={cancelingId === post.id}
                          >
                            <FaTrash className="me-1" />
                            {cancelingId === post.id ? "Cancelling…" : "Cancel"}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Platforms line – always shown */}
                    <div className="mb-1">
                      <strong>Platforms:</strong> {platforms || "—"}
                    </div>

                    {/* Content snippet – ONLY in full (non-embedded) view */}
                    {contentPreview && (
                      <p className="mb-2 text-muted small">
                        {contentPreview}
                      </p>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* "View All" button - only shown in embedded mode when there are visible posts */}
      {embedded && hasVisiblePosts && (
        <div className="text-center mb-3">
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={handleViewAll}
          >
            <FaEye className="me-1" />
            VIEW ALL SCHEDULED POSTS
          </button>
        </div>
      )}

      {/* Pagination - only in standalone mode */}
      {!embedded && pagination.hasMore && (
        <div className="text-center mb-3">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={onLoadMore}
            disabled={loadingMore}
          >
            {loadingMore ? "Loading…" : "Load more"}
          </button>
        </div>
      )}

      {/* Footer – keep existing behavior for standalone views.
          In embedded mode (Dashboard) we hide this so you don't get a nested footer. */}
      {!embedded && (
        <AppFooter
          buttons={[
            {
              icon: <FaArrowLeft />,
              label: "Back",
              onClick: () => navigate(-1),
            },
            {
              icon: <FaList />,
              label: "Event List",
              onClick: () => navigate(`/${tenantId}/events`),
            },
            {
              icon: <FaCalendarPlus />,
              label: "New schedule",
              onClick: () =>
                navigate(`/${tenantFromRoute || tenantId}/media/schedule`),
            },
          ]}
        />
      )}
    </div>
  );
}